import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { create } from 'zustand';
import { casesApi, type Task } from '../api';
import { intlLocale, t } from '../i18n';
import { route } from '../lib/countries';
import { cx } from '../lib/cx';
import { formatDate, formatDateTime } from '../lib/date';
import { errorText } from '../lib/errors';
import { toast } from '../stores/toast';
import { Button, Modal } from './ui';

export const isTaskDone = (x: Task) => !!x.done_at || !!x.done;

/** Нотатка «Що потрібно» з шаблону-прикладу (сід): починається з «Приклад:» — її варто переписати під свою компанію. */
export const EXAMPLE_PREFIX = 'Приклад:';
export const isExampleNote = (note: string | null | undefined) => !!note && note.trimStart().startsWith(EXAMPLE_PREFIX);

/** Позначити задачу виконаною / повернути — з оновленням усіх місць, де вона видна. */
export function useTaskToggle() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const toggle = async (x: Task, done: boolean) => {
    setBusy(x.id);
    try {
      await casesApi.patchTask(x.id, { done });
      for (const k of [['today'], ['tasks'], ['case-tasks'], ['pipeline'], ['case-events']]) void qc.invalidateQueries({ queryKey: k });
      return true;
    } catch (e) {
      toast.error(errorText(e));
      return false;
    } finally {
      setBusy(null);
    }
  };
  return { toggle, busy };
}

// ---------------------------------------------------------------- вікно задачі
interface TaskDialogState {
  task: Task | null;
  open: (task: Task) => void;
  close: () => void;
}
export const useTaskDialog = create<TaskDialogState>((set) => ({
  task: null,
  open: (task) => set({ task }),
  close: () => set({ task: null }),
}));
export const openTask = (task: Task) => useTaskDialog.getState().open(task);

/** Термін задачі: 23:59 за Києвом — «до кінця дня» (так ставить шаблон плану на сервері), інакше дата з часом. */
const KYIV = 'Europe/Kyiv';
function formatDue(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return formatDateTime(iso);
  const hm = new Intl.DateTimeFormat('en-GB', { timeZone: KYIV, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
  if (hm !== '23:59') return formatDateTime(iso);
  const day = new Intl.DateTimeFormat(intlLocale(), { timeZone: KYIV, day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
  return t('task.dueEndOfDay', { d: day });
}

/** Що саме треба зробити: справа, крок плану, вимога, на що зважити, термін, хто. Відмітка — тут явною кнопкою. */
export function TaskDialog() {
  const { task, close, open } = useTaskDialog();
  const { toggle, busy } = useTaskToggle();
  if (!task) return null;
  const done = isTaskDone(task);
  const late = !done && !!task.due_at && new Date(task.due_at).getTime() < Date.now();
  const caseLine = [task.case_number, task.case_origin || task.case_dest ? route(task.case_origin ?? null, task.case_dest ?? null) : task.case_title, task.deceased_name]
    .filter(Boolean)
    .join(' · ');
  const flip = async () => {
    if (await toggle(task, !done)) open({ ...task, done: !done, done_at: done ? null : new Date().toISOString() });
  };
  return (
    <Modal
      open
      onClose={close}
      title={task.title}
      footer={
        <>
          {task.case_id && (
            <Link className="btn btn-ghost" to={`/cases/${task.case_id}`} onClick={close}>
              {t('task.openCase')}
            </Link>
          )}
          <Button variant={done ? 'outline' : 'solid'} busy={busy === task.id} onClick={() => void flip()}>
            {done ? t('task.reopen') : t('task.markDone')}
          </Button>
        </>
      }
    >
      <dl className="m-0 grid grid-cols-[minmax(110px,150px)_1fr] gap-x-4 gap-y-2.5">
        <dt className="text-lab">{t('common.status')}</dt>
        <dd className="m-0 font-semibold">{done ? t('task.doneAt', { d: task.done_at ? formatDate(task.done_at) : '' }) : t('task.notDone')}</dd>
        {caseLine && (
          <>
            <dt className="text-lab">{t('tasks.case')}</dt>
            <dd className="m-0">{caseLine}</dd>
          </>
        )}
        {task.block_title && (
          <>
            <dt className="text-lab">{t('task.step')}</dt>
            <dd className="m-0">
              {task.block_position != null ? `${task.block_position + 1}. ` : ''}
              {task.block_title}
            </dd>
          </>
        )}
        <dt className="text-lab">{t('tasks.due')}</dt>
        <dd className={cx('m-0', late && 'text-danger font-semibold')}>{task.due_at ? formatDue(task.due_at) : t('today.noDue')}</dd>
        <dt className="text-lab">{t('common.assignee')}</dt>
        <dd className="m-0">{task.assignee_name ?? t('common.unassigned')}</dd>
      </dl>
      {(task.requirement_note || task.block_nuance) && (
        <div className="mt-5 flex flex-col gap-3">
          {task.requirement_note && (
            <div>
              <div className="label mb-1">{t('task.what')}</div>
              <p className="m-0 leading-relaxed">{task.requirement_note}</p>
              {/* нотатка з шаблону-прикладу: підказуємо, де її переписати під свою компанію */}
              {isExampleNote(task.requirement_note) && <p className="m-0 mt-1 text-xs text-lab">{t('task.whatNeededExample')}</p>}
            </div>
          )}
          {task.block_nuance && (
            <div>
              <div className="label mb-1">{t('pipe.nuance')}</div>
              <p className="m-0 text-lab leading-relaxed">{task.block_nuance}</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- рядок задачі
/**
 * Квадратик — окрема кнопка «виконано»; клік по назві відкриває задачу. Так задача не закривається випадково.
 */
export function TaskRow({ task, showCase, showAssignee }: { task: Task; showCase?: boolean; showAssignee?: boolean }) {
  const { toggle, busy } = useTaskToggle();
  const done = isTaskDone(task);
  const late = !done && !!task.due_at && new Date(task.due_at).getTime() < Date.now();
  const meta = [
    showCase && task.case_number ? t('today.inCase', { n: task.case_number }) : null,
    showAssignee ? task.assignee_name ?? t('common.unassigned') : null,
    done && task.done_at ? t('task.doneAt', { d: formatDate(task.done_at) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="row">
      <input
        type="checkbox"
        className="cb !w-[18px] !h-[18px] flex-none"
        aria-label={done ? t('task.reopenAria', { title: task.title }) : t('task.markDoneAria', { title: task.title })}
        checked={done}
        disabled={busy === task.id}
        onChange={(e) => void toggle(task, e.target.checked)}
      />
      <button type="button" className="flex-1 min-w-0 flex flex-col gap-0.5 text-left group" onClick={() => openTask(task)}>
        <span className={cx('group-hover:underline underline-offset-4 leading-snug', done && 'line-through text-lab')}>{task.title}</span>
        {meta && <span className="text-xs text-lab truncate">{meta}</span>}
      </button>
      {!done && (
        <span className={cx('chip flex-none', late && 'chip-danger')}>{task.due_at ? formatDate(task.due_at) : t('today.noDue')}</span>
      )}
    </li>
  );
}

/** Задачі однієї справи, згруповані за кроками плану: видно, що вже зроблено і що ні. */
export function TasksByStep({ tasks, showAssignee }: { tasks: Task[]; showAssignee?: boolean }) {
  const groups = new Map<string, { title: string; pos: number; items: Task[] }>();
  for (const x of tasks) {
    const key = x.case_block_id ?? 'other';
    const g = groups.get(key) ?? { title: x.block_title ?? t('task.other'), pos: x.block_position ?? 9999, items: [] };
    g.items.push(x);
    groups.set(key, g);
  }
  const ordered = [...groups.values()].sort((a, b) => a.pos - b.pos);
  const sortTasks = (a: Task, b: Task) => Number(isTaskDone(a)) - Number(isTaskDone(b)) || (a.due_at ?? '9').localeCompare(b.due_at ?? '9');
  return (
    <div className="flex flex-col gap-3">
      {ordered.map((g) => {
        const doneN = g.items.filter(isTaskDone).length;
        return (
          <div key={`${g.pos}:${g.title}`}>
            {ordered.length > 1 && (
              <div className="flex justify-between gap-2 text-xs text-lab pt-1">
                <span className="font-semibold text-fg truncate">
                  {g.pos < 9999 ? `${g.pos + 1}. ` : ''}
                  {g.title}
                </span>
                <span className="tabular-nums flex-none">
                  {doneN}/{g.items.length}
                </span>
              </div>
            )}
            <ul className="m-0 p-0 list-none">
              {[...g.items].sort(sortTasks).map((x) => (
                <TaskRow key={x.id} task={x} showAssignee={showAssignee} />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
